import React, { useState, useRef, useEffect } from 'react';
import CookiesCloseIcon from '@/assets/icons/cookiesCloseIcon.svg';
import { Button, DialogTitle, IconButton, DialogContent } from '@mui/material';
import { styled } from '@mui/system';
import UKDialog from '@/components/UKDialog';
import SettingList from './components/settingList';
import CloseIcon from '@mui/icons-material/Close';
import styles from './index.module.scss';
import cookieUtils from '@/intl-jdi-common-ssr/utils/cookieUtils';
import { useTranslation } from '@/i18n/client';
import { getSettingList, setCookieConsent } from '@/intl-jdi-common-ssr/constants/cookie';
import cn from 'clsx';

interface Props {
  openDialog: boolean;
  openManager: boolean;
  closeDialog: () => void;
  closeManager: () => void;
}

interface UserCookie {
  [key: string]: number;
}

interface SettingListType {
  getUserCookie: () => UserCookie;
}

const CookiesManage = ({ openDialog, openManager, closeDialog, closeManager }: Props) => {
  const [isShowMange, setIsShowMange] = useState<boolean>(false);
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const settingListRef = useRef<SettingListType | null>(null);
  const { t } = useTranslation();
  const CookiesSettingList = getSettingList(t).CookiesSettingList;

  useEffect(() => {
    setIsOpenDialog(openDialog);
  }, [openDialog]);

  useEffect(() => {
    setIsShowMange(openManager);
  }, [openManager]);

  const handleClose = () => {
    setIsOpenDialog(false);
    closeDialog?.();
  };

  const handleCloseManager = () => {
    setIsShowMange(false);
    closeManager?.();
  };

  const handleConfirmChoies = () => {
    if (settingListRef.current) {
      const userCookie = settingListRef.current?.getUserCookie();
      setCookieConsent(userCookie);
      handleClose();
      handleCloseManager();
    }
  };

  const handleRejectAll = () => {
    const rejectAllUserCookie: UserCookie = {};
    CookiesSettingList.forEach((item) => {
      rejectAllUserCookie[item.key] = 0;
    });
    setCookieConsent(rejectAllUserCookie);
    handleCloseManager();
  };

  const handleAllowAll = () => {
    const allowAllUserCookie: UserCookie = {};
    CookiesSettingList.forEach((item) => {
      allowAllUserCookie[item.key] = 1;
    });
    setCookieConsent(allowAllUserCookie);
    handleCloseManager();
  };

  const handleHideManage = () => {
    handleCloseManager();
    cookieUtils.setSessionStorage('isShowCookiesManage', '0');
  };

  return (
    <div
      className={cn(
        styles.cookiesWrapper,
        isShowMange ? styles.showCookiesManage : styles.hideCookiesManage,
      )}
    >
      <div className={styles.mainWrapper}>
        <CookiesCloseIcon className={styles.close} onClick={handleHideManage} />
        <div className={styles.title}>{t('cookie.manageCookies')}</div>
        <div className={styles.content}>
          {t('cookie.manageCookiesDescription')}{' '}
          <a className={styles.link} href='/help/cookie-policy' target='_self'>
            {t('cookie.more')}
          </a>
        </div>
        <div className={styles.btnWrapper}>
          <Button
            variant='contained'
            disableElevation
            sx={{
              color: '#FFF',
              height: '32px',
              backgroundColor: 'transparent',
              textTransform: 'none',
              border: '1px solid #FFF',
              marginRight: '16px',
              lineHeight: '18px',
              padding: '6px 15px',
            }}
            onClick={() => setIsOpenDialog(true)}
          >
            {t('cookie.manageCookies')}
          </Button>
          <Button
            variant='contained'
            disableElevation
            sx={{
              color: '#FFF',
              height: '32px',
              backgroundColor: 'transparent',
              textTransform: 'none',
              border: '1px solid #FFF',
              marginRight: '16px',
              lineHeight: '18px',
              padding: '6px 15px',
            }}
            onClick={handleRejectAll}
          >
            {t('cookie.rejectAll')}
          </Button>
          <Button
            size='small'
            variant='contained'
            disableElevation
            sx={{
              color: '#',
              height: '32px',
              backgroundColor: '#CC0C1C',
              textTransform: 'none',
              border: 'none',
              lineHeight: '18px',
              padding: '6px 15px',
            }}
            onClick={handleAllowAll}
          >
            {t('cookie.allowAll')}
          </Button>
        </div>
      </div>
      <UKDialog
        open={isOpenDialog}
        onClose={handleClose}
        showCloseBtn={false}
        sx={{
          '&': {
            zIndex: 2002,
          },
          '& .MuiDialog-paper': {
            width: '800px',
            maxWidth: 'none',
            borderRadius: '12px',
            zIndex: 2003,
          },
          '& .css-1cennmq-MuiBackdrop-root-MuiDialog-backdrop': {
            zIndex: 2002,
          },
        }}
      >
        <DialogTitle
          sx={{
            height: '20px',
            padding: '0 24px',
            fontSize: '16px',
            lineHeight: '20px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            marginTop: '20px',
          }}
        >
          {t('cookie.manageCookies')}
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <CustomerDialogContent>
          {t('cookie.manageCookiesDescription')}{' '}
          <a href='/help/cookie-policy' target='_self'>
            {t('cookie.more')}
          </a>
          <SettingList ref={settingListRef} />
        </CustomerDialogContent>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: '12px',
            boxSizing: 'border-box',
            padding: '24px',
          }}
        >
          <Button
            size='small'
            variant='contained'
            disableElevation
            sx={{
              color: '#FFF',
              height: '32px',
              backgroundColor: '#CC0C1C',
              textTransform: 'none',
              border: 'none',
              lineHeight: '18px',
              fontSize: '14px',
              padding: '7px 25px',
              borderRadius: '4px',
            }}
            onClick={handleConfirmChoies}
          >
            {t('cookie.confirmMyChoices')}
          </Button>
        </div>
      </UKDialog>
    </div>
  );
};

const CustomerDialogContent = styled(DialogContent)({
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '18px',
  color: '#888B94',
  '& a': {
    color: '#1869F5',
    textDecoration: 'underline',
  },
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#fff',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0, 0, 0, 0.14)',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.28)',
  },
});

export default CookiesManage;
